import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { getApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquotaCurrent } from '@/api/generated/company-user/company-user'
import type { EmployeeUserResponse, VacationQuotaModel } from '@/api/model'

/** Absence metrics of an employee — taken verbatim from the current vacation quota. */
export interface EmployeeAbsenceSummary {
  /** Vacation days of the quota. */
  totalVacationDays: number | null
  /** Remaining vacation days (backend-calculated). */
  surplus: number | null
  /** Carryover from the previous quota. */
  surplusFromPrevious: number | null
  /** Planned vacation days (backend-calculated). */
  plannedDays: number | null
  /** Taken vacation days (backend-calculated). */
  takenDays: number | null
}

/** Loading state + result per employee, so the table can react per row. */
export interface AbsenceSummaryState {
  summary?: EmployeeAbsenceSummary
  isPending: boolean
  isError: boolean
}

/**
 * Metrics of an employee from the current vacation quota — `loadSurplus=true` makes the
 * backend calculate surplus/planned/taken days; nothing is derived on the client.
 */
async function loadSummary(
  serviceModuleId: number,
  employeeId: string,
): Promise<EmployeeAbsenceSummary> {
  const quota = await getApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquotaCurrent(
    serviceModuleId,
    { employeeId, loadSurplus: true },
  )
    // 204 (no quota for this employee) arrives as an empty body.
    .then((r) => (r.data || undefined) as VacationQuotaModel | undefined)
    .catch(() => undefined)

  return {
    totalVacationDays: quota?.totalVacationDays ?? null,
    surplus: quota?.surplus ?? null,
    surplusFromPrevious: quota?.surplusFromPrevious ?? null,
    plannedDays: quota?.plannedDays ?? null,
    takenDays: quota?.takenDays ?? null,
  }
}

/**
 * Loads the absence metrics for a list of employees via fan-out — one React Query per
 * employee (its current vacation quota). Returns a map (employee id → state), so the
 * table can look up loading state/value per row.
 */
export function useAbsenceSummaries(serviceModuleId: number, employees: EmployeeUserResponse[]) {
  const results = useQueries({
    queries: employees.map((employee) => ({
      queryKey: ['absence-summary', serviceModuleId, employee.id] as const,
      queryFn: () => loadSummary(serviceModuleId, employee.id ?? ''),
      enabled: !!employee.id,
      staleTime: 60_000,
    })),
  })

  const summariesById = useMemo(() => {
    const map = new Map<string, AbsenceSummaryState>()
    employees.forEach((employee, index) => {
      if (!employee.id) return
      const result = results[index]
      map.set(employee.id, {
        summary: result?.data,
        isPending: result?.isPending ?? false,
        isError: result?.isError ?? false,
      })
    })
    return map
  }, [employees, results])

  return { summariesById }
}
